import { useEffect, useRef } from 'react';
import type { JSX } from 'react';

import { safePlay } from './media';
import type { AttachableTrack } from './roomState';

interface AudioSinkProps {
  readonly track: AttachableTrack;
  /**
   * Audio-output device id (picked on the prejoin screen) routed via setSinkId. Best-effort:
   * unsupported browsers and jsdom (no setSinkId) keep the system default; a failing switch is
   * warned and ignored.
   */
  readonly sinkId?: string | null | undefined;
}

/** Hidden <audio> element that plays exactly one remote audio track. */
export function AudioSink({ track, sinkId }: AudioSinkProps): JSX.Element {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const element = audioRef.current;
    if (element === null) {
      return;
    }
    track.attach(element);
    safePlay(element);
    return () => {
      track.detach(element);
    };
  }, [track]);

  // Runs after the attach effect (declaration order), re-runs when the pick changes.
  useEffect(() => {
    const element = audioRef.current;
    if (element === null || sinkId == null || sinkId.length === 0) {
      return;
    }
    const sinkCapable = element as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
    if (typeof sinkCapable.setSinkId !== 'function') {
      // jsdom and browsers without setSinkId: keep the system default output.
      return;
    }
    sinkCapable.setSinkId(sinkId).catch((err: unknown) => {
      console.warn(`audio output switch to ${sinkId} failed; keeping the previous output`, err);
    });
  }, [sinkId]);

  return <audio ref={audioRef} autoPlay className="hidden" />;
}
